import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, NetworkError, api, isChallenge, type AuthChallenge, type Membership, type Session, type SignInResult } from '../api';
import { useI18n } from '../i18n';
import { Field, Spinner } from '../components';
import { LOCALES, LOCALE_LABEL } from '@oxyflows/i18n';
import { FlowLine, Logo, XMark } from '../brand';
import clinicPhoto from '../assets/brand/clinic-login.webp';

/**
 * Demo accounts, shown only in development. The API refuses to start in
 * production with `ALLOW_DEMO_ACCOUNTS=true`, and this panel is compiled out of
 * a production build by the `import.meta.env.DEV` check below.
 */
const DEMO_ACCOUNTS = [
  { email: 'owner@clinic-a.test', who: 'Owner · Noor Dental Center (Center plan)' },
  { email: 'rania@example.test', who: 'Owns one clinic, restricted dentist in another' },
  { email: 'reception@clinic-a.test', who: 'Receptionist · scheduling only' },
  { email: 'accounts@clinic-a.test', who: 'Accountant · no clinical notes' },
  // The vendor's own account. Not a clinic role: it sees every clinic, creates
  // them, and issues activation codes — and it holds no clinical permission at
  // all, which is the point.
  { email: 'platform@oxyflows.test', who: 'Platform operator · every clinic, no patient records' },
];
const DEMO_PASSWORD = 'oxy-demo-2026';

/**
 * Where an email link lands: `?invite=` opens account creation, `?activate=`
 * opens clinic activation, each with the address filled in.
 */
function fromLink(): { mode: 'register' | 'activate'; email: string } | null {
  const q = new URLSearchParams(window.location.search);
  const invite = q.get('invite');
  const activate = q.get('activate');
  if (invite) return { mode: 'register', email: invite };
  if (activate) return { mode: 'activate', email: activate };
  return null;
}

export function Login({ onSignedIn }: { onSignedIn: (session: Session) => void }) {
  const { t, tx, locale, setLocale } = useI18n();
  const [link] = useState(fromLink);
  // The public demo (/demo/) publishes its one account and fills it in.
  const demo = window.oxyDemo?.login;
  const [email, setEmail] = useState(link?.email ?? demo?.email ?? '');
  const [password, setPassword] = useState(demo?.password ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingClinics, setPendingClinics] = useState<{ session: Session; memberships: Membership[] } | null>(null);
  // Activation: the first sign-in of someone who was invited or whose clinic
  // was just created. They have a code, not a password.
  const [mode, setMode] = useState<'signin' | 'activate' | 'register' | 'forgot'>(link?.mode ?? 'signin');
  // The second step: a code is on its way.
  const [challenge, setChallenge] = useState<AuthChallenge | null>(null);
  const [otp, setOtp] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [code, setCode] = useState('');
  const [fullName, setFullName] = useState('');
  const [confirm, setConfirm] = useState('');
  const [acceptPolicy, setAcceptPolicy] = useState(false);

  // The link has done its job; a reload should not reopen the form.
  useEffect(() => {
    if (link) window.history.replaceState(null, '', window.location.pathname);
  }, [link]);

  // The resend button counts down.
  useEffect(() => {
    if (!challenge) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [challenge]);
  const waitSeconds = challenge ? Math.max(0, Math.ceil((Date.parse(challenge.resendAfter) - now) / 1000)) : 0;

  function fail(err: unknown) {
    if (err instanceof NetworkError) setError(t.errors.network);
    else if (err instanceof ApiError) setError(err.message);
    else setError(t.common.error);
  }

  /** A session, or a code to ask for first. */
  function handle(result: SignInResult) {
    if (isChallenge(result)) {
      setChallenge(result.challenge);
      setOtp('');
      setNotice(null);
      setError(null);
      return;
    }
    landed(result);
  }

  function backToSignIn() {
    setChallenge(null);
    setOtp('');
    setNotice(null);
    setError(null);
    setPassword('');
    setConfirm('');
    setMode('signin');
  }

  async function verifyCode(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    if (challenge.purpose === 'reset' && password !== confirm) {
      setError(t.auth.passwordsDiffer);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const session =
        challenge.purpose === 'reset'
          ? await api.resetPassword({ challengeId: challenge.id, code: otp, newPassword: password })
          : await api.verifyCode({ challengeId: challenge.id, code: otp, trustDevice: challenge.trustOffered && trustDevice });
      setChallenge(null);
      landed(session);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.resendCode(challenge.id);
      setChallenge(r.challenge);
      setOtp('');
      setNotice(t.auth.codeSent);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  async function forgot(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.forgotPassword(email.trim());
      setPassword('');
      setConfirm('');
      setChallenge(r.challenge);
      setOtp('');
      setNotice(null);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  function landed(session: Session) {
    if (!session.activeClinicId && session.memberships.length > 1) {
      setPendingClinics({ session, memberships: session.memberships });
      return;
    }
    onSignedIn(session);
  }

  async function activate(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError(t.auth.passwordsDiffer);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const session = await api.activate({
        email: email.trim(),
        code: code.trim(),
        password,
        acceptPolicy: true,
        ...(fullName.trim() ? { fullName: fullName.trim() } : {}),
      });
      handle(session);
    } catch (err) {
      if (err instanceof NetworkError) setError(t.errors.network);
      else if (err instanceof ApiError) setError(err.message);
      else setError(t.common.error);
    } finally {
      setBusy(false);
    }
  }

  async function register(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError(t.auth.passwordsDiffer);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const session = await api.register({ email: email.trim(), password, fullName: fullName.trim() });
      handle(session);
    } catch (err) {
      if (err instanceof NetworkError) setError(t.errors.network);
      else if (err instanceof ApiError) setError(err.message);
      else setError(t.common.error);
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // A code may be due first; a user in several clinics then picks one
      // before the shell loads (spec §4) — both handled by handle().
      handle(await api.login(email.trim(), password));
    } catch (err) {
      if (err instanceof NetworkError) setError(t.errors.network);
      else if (err instanceof ApiError) setError(err.message);
      else setError(t.common.error);
    } finally {
      setBusy(false);
    }
  }

  async function chooseClinic(clinicId: string) {
    setBusy(true);
    try {
      await api.switchClinic(clinicId);
      const session = await api.me();
      onSignedIn(session);
    } catch {
      setError(t.common.error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      {/* The brand's clinic photograph, and its line. */}
      <aside className="login-aside">
        <img src={clinicPhoto} alt="" />
        <div className="login-aside-caption">
          <div className="tagline">{t.brand.tagline}</div>
          <p>{t.brand.overview}</p>
          {/* Said only where it is true: a development build. */}
          {import.meta.env.DEV ? (
            <p className="dev-note">
              {locale === 'ar' ? 'بيئة تطوير محلية · بيانات تجريبية فقط' : 'Local development · synthetic data only'}
            </p>
          ) : null}
        </div>
      </aside>

      <div className="login-panel">
        <XMark className="login-corner-x" />
        <FlowLine className="login-corner-line" />
        <div className="login-form">
          <Logo className="login-logo" />
          <div className="login-tagline">{t.brand.tagline}</div>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 26 }}>
            <h1>{t.auth.signIn}</h1>
            <div className="row" style={{ gap: 4 }}>
              {LOCALES.map((l) => (
                <button
                  key={l}
                  type="button"
                  className={l === locale ? 'btn btn-sm' : 'btn btn-ghost btn-sm'}
                  onClick={() => setLocale(l)}
                  aria-pressed={l === locale}
                >
                  {LOCALE_LABEL[l]}
                </button>
              ))}
            </div>
          </div>

          {challenge && !pendingClinics ? (
            <form onSubmit={verifyCode} className="stack" noValidate>
              <div>
                <h2>{challenge.purpose === 'reset' ? t.auth.resetTitle : t.auth.codeTitle}</h2>
                <p className="page-sub">{tx(challenge.purpose === 'reset' ? t.auth.resetHint : t.auth.codeHint, { to: challenge.to })}</p>
              </div>
              {error ? (
                <div className="notice danger" role="alert">
                  {error}
                </div>
              ) : null}
              {notice ? (
                <div className="notice ok" role="status">
                  {notice}
                </div>
              ) : null}
              <Field label={t.auth.codeLabel} required>
                <input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="ltr-value code-input"
                  placeholder="000000"
                  aria-describedby="code-hint"
                  autoFocus
                  required
                />
              </Field>
              {challenge.purpose === 'reset' ? (
                <>
                  <Field label={t.auth.newPassword} hint={t.auth.passwordRule} required>
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" className="ltr-value" required />
                  </Field>
                  <Field label={t.auth.confirmPassword} required>
                    <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" className="ltr-value" required />
                  </Field>
                </>
              ) : null}
              {challenge.trustOffered ? (
                <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
                  <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} style={{ marginTop: 3 }} />
                  <span style={{ fontSize: 13 }}>
                    <strong style={{ fontWeight: 600 }}>{t.auth.trustDevice}</strong>
                    <span id="code-hint" style={{ display: 'block', color: 'var(--ink-3)' }}>{t.auth.trustHint}</span>
                  </span>
                </label>
              ) : null}
              {import.meta.env.DEV && challenge.devCode ? (
                <p className="hint" style={{ margin: 0, color: 'var(--warn)', fontSize: 12 }}>{tx(t.auth.devCode, { code: challenge.devCode })}</p>
              ) : null}
              <button
                type="submit"
                className="btn"
                disabled={busy || otp.length !== 6 || (challenge.purpose === 'reset' && (!password || !confirm))}
              >
                {busy ? <Spinner /> : challenge.purpose === 'reset' ? t.auth.resetButton : t.auth.verify}
              </button>
              <button type="button" className="btn-link" disabled={busy || waitSeconds > 0} onClick={() => void resend()}>
                {waitSeconds > 0 ? tx(t.auth.resendIn, { s: waitSeconds }) : t.auth.resend}
              </button>
              <button type="button" className="btn-link" onClick={backToSignIn}>
                {t.auth.backToSignIn}
              </button>
            </form>
          ) : mode === 'forgot' && !pendingClinics ? (
            <form onSubmit={forgot} className="stack" noValidate>
              <div>
                <h2>{t.auth.forgotTitle}</h2>
                <p className="page-sub">{t.auth.forgotHint}</p>
              </div>
              {error ? (
                <div className="notice danger" role="alert">
                  {error}
                </div>
              ) : null}
              <Field label={t.auth.email} required>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.auth.emailPlaceholder} autoComplete="username" className="ltr-value" required autoFocus />
              </Field>
              <button type="submit" className="btn" disabled={busy || !email}>
                {busy ? <Spinner /> : t.auth.sendCode}
              </button>
              <button type="button" className="btn-link" onClick={backToSignIn}>
                {t.auth.backToSignIn}
              </button>
            </form>
          ) : mode === 'register' && !pendingClinics ? (
            <form onSubmit={register} className="stack" noValidate>
              <div>
                <h2>{t.auth.registerTitle}</h2>
                <p className="page-sub">{t.auth.registerHint}</p>
              </div>
              {error ? (
                <div className="notice danger" role="alert">
                  {error}
                </div>
              ) : null}
              <Field label={t.auth.fullName} required>
                <input value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="name" autoFocus />
              </Field>
              <Field label={t.auth.email} required>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.auth.emailPlaceholder} autoComplete="username" className="ltr-value" required />
              </Field>
              <Field label={t.auth.newPassword} hint={t.auth.passwordRule} required>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" className="ltr-value" required />
              </Field>
              <Field label={t.auth.confirmPassword} required>
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" className="ltr-value" required />
              </Field>
              <button type="submit" className="btn" disabled={busy || !email || !fullName || !password || !confirm}>
                {busy ? <Spinner /> : t.auth.createAccount}
              </button>
              <button type="button" className="btn-link" onClick={() => { setMode('signin'); setError(null); }}>
                {t.auth.backToSignIn}
              </button>
            </form>
          ) : mode === 'activate' && !pendingClinics ? (
            <form onSubmit={activate} className="stack" noValidate>
              <div>
                <h2>{t.auth.activateTitle}</h2>
                <p className="page-sub">{t.auth.activateHint}</p>
              </div>
              {error ? (
                <div className="notice danger" role="alert">
                  {error}
                </div>
              ) : null}
              <Field label={t.auth.email} required>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.auth.emailPlaceholder} autoComplete="username" className="ltr-value" required autoFocus />
              </Field>
              <Field label={t.auth.code} required>
                <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="OXY-XXXXX-XXXXX-XXXXX" className="ltr-value" autoComplete="one-time-code" required />
              </Field>
              <Field label={t.auth.fullName}>
                <input value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="name" />
              </Field>
              <Field label={t.auth.newPassword} hint={t.auth.passwordRule} required>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" className="ltr-value" required />
              </Field>
              <Field label={t.auth.confirmPassword} required>
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" className="ltr-value" required />
              </Field>
              {/* The clinic agrees to where its records live before it stores one. */}
              <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
                <input type="checkbox" checked={acceptPolicy} onChange={(e) => setAcceptPolicy(e.target.checked)} style={{ marginTop: 3 }} />
                <span style={{ fontSize: 13 }}>
                  {t.auth.acceptPolicy}{' '}
                  <a href="/policy" target="_blank" rel="noreferrer">{t.auth.policyLink}</a>
                </span>
              </label>
              <button type="submit" className="btn" disabled={busy || !email || !code || !password || !confirm || !acceptPolicy}>
                {busy ? <Spinner /> : t.auth.activate}
              </button>
              <button type="button" className="btn-link" onClick={() => { setMode('signin'); setError(null); }}>
                {t.auth.backToSignIn}
              </button>
            </form>
          ) : pendingClinics ? (
            <div className="stack">
              <div>
                <h2>{t.auth.chooseClinic}</h2>
                <p className="page-sub">{t.auth.chooseClinicHint}</p>
              </div>
              <div className="demo-list">
                {pendingClinics.memberships.map((m) => (
                  <button
                    key={m.clinicId}
                    type="button"
                    className="demo-account"
                    onClick={() => void chooseClinic(m.clinicId)}
                    disabled={busy}
                  >
                    <span>
                      <strong>{locale === 'ar' ? m.nameAr : m.nameEn}</strong>
                      <span className="who" style={{ display: 'block' }}>
                        {m.roles.join(', ')}
                      </span>
                    </span>
                    <span className="badge brand">{m.plan}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="stack" noValidate>
              {error ? (
                <div className="notice danger" role="alert">
                  {error}
                </div>
              ) : null}

              <Field label={t.auth.email} required>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.auth.emailPlaceholder}
                  autoComplete="username"
                  className="ltr-value"
                  required
                  autoFocus
                />
              </Field>

              <Field label={t.auth.password} required>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="ltr-value"
                  required
                />
              </Field>

              <button type="submit" className="btn" disabled={busy || !email || !password}>
                {busy ? (
                  <>
                    <Spinner /> {t.auth.signingIn}
                  </>
                ) : (
                  t.auth.signIn
                )}
              </button>
              {demo ? (
                <div className="notice demo-credentials">
                  <strong>{locale === 'ar' ? 'حساب النسخة التجريبية' : 'The demo account'}</strong>
                  <span>
                    {locale === 'ar' ? 'البريد' : 'Username'} <code className="ltr-value">{demo.email}</code> · {locale === 'ar' ? 'كلمة المرور' : 'Password'} <code className="ltr-value">{demo.password}</code>
                  </span>
                  <span className="hint">
                    {locale === 'ar'
                      ? 'عيادة تجريبية بمرضى ومواعيد وفواتير. جرّب كل شيء: لا شيء يُحفظ على خوادمنا.'
                      : 'A sample clinic with patients, appointments and invoices. Try anything: nothing is saved on our servers.'}
                  </span>
                </div>
              ) : (
                <>
                  <button type="button" className="btn-link" onClick={() => { setMode('forgot'); setError(null); setPassword(''); }}>
                    {t.auth.forgotLink}
                  </button>
                  <button type="button" className="btn-link" onClick={() => { setMode('register'); setError(null); setPassword(''); }}>
                    {t.auth.invitedLink}
                  </button>
                  <button type="button" className="btn-link" onClick={() => { setMode('activate'); setError(null); setPassword(''); }}>
                    {t.auth.ownerLink}
                  </button>
                </>
              )}
              <a className="btn-link" href="/policy" target="_blank" rel="noreferrer">
                {t.auth.policyLink}
              </a>

              {import.meta.env.DEV && !demo ? (
                <div style={{ marginTop: 18 }}>
                  <div className="section-label">{t.auth.demoTitle}</div>
                  <p className="hint" style={{ margin: '4px 0 0', color: 'var(--ink-3)', fontSize: 12 }}>
                    {t.auth.demoHint} <code>{DEMO_PASSWORD}</code>
                  </p>
                  <div className="demo-list">
                    {DEMO_ACCOUNTS.map((a) => (
                      <button
                        key={a.email}
                        type="button"
                        className="demo-account"
                        onClick={() => {
                          setEmail(a.email);
                          setPassword(DEMO_PASSWORD);
                        }}
                      >
                        <span>
                          <code className="ltr-value">{a.email}</code>
                          <span className="who" style={{ display: 'block' }}>
                            {a.who}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* True of a development machine only; production holds real clinics. */}
              {import.meta.env.DEV && !demo ? (
                <p className="hint" style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 6 }}>
                  {tx(locale === 'ar' ? 'لا توجد بيانات مرضى حقيقية في هذه البيئة.' : 'No real patient data exists in this environment.')}
                </p>
              ) : null}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
